import { Tooltip } from 'antd';
import { CSSProperties } from 'react';
import styled from 'styled-components';

import { BodilColor } from '~src/branding';
import { Checkbox } from '~src/components/CheckBox';
import { Heading, Text } from '~src/components/Typography';
import { fuelMap } from '~src/constants';
import { APARTMENT_TAKEOVER_TEXT, HIGH_USAGE_TAKEOVER_TEXT, INVALID_HEATING_TEXT } from '~src/constants/takeover';
import { useSelectedProductTypesContext } from '~src/context/selectedProductTypeContext';
import { ProductType, ProductTypes, TakeoverType } from '~src/model';

const TabWrapper = styled.div({
  position: 'relative',
  display: 'flex',
  color: 'white',
  backgroundColor: 'transparent',
  width: 'fit-content',
  alignContent: 'center',
});

const Background = styled.div({
  position: 'absolute',
  width: '100%',
  height: '100%',
  backgroundColor: BodilColor.base.almond,
  opacity: 0.2,
  borderRadius: '0 0 24px 24px',
});

const TabButton = styled.div<{ disabled?: boolean }>(({ disabled }) => ({
  color: BodilColor.base.white,
  position: 'relative',
  padding: '8px 16px 5px 16px',
  background: 'none',
  border: 'none',
  cursor: disabled ? 'not-allowed' : 'pointer',
  ...(disabled && { opacity: 0.4 }),
  margin: 'auto',
}));

const CheckBoxWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  margin: '16px 32px 16px 0',
});

const Row = styled.div<{ disabled?: boolean }>(({ disabled }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  cursor: disabled ? 'not-allowed' : 'inherit',
  width: 'fit-content',
}));

type OfferTypeSelectorProps = {
  style?: CSSProperties;
  backgroundStyle?: CSSProperties;
  buttonStyle?: CSSProperties;
  fuelType?: string;
  type?: 'button' | 'checkmark';
};

export const OfferTypeSelector = ({
  style,
  backgroundStyle,
  buttonStyle,
  fuelType,
  type = 'button',
}: OfferTypeSelectorProps) => {
  const {
    selectedProductTypes,
    setSelectedProductTypes,
    disabledProducts: disabledTabs,
    takeover,
  } = useSelectedProductTypesContext();

  const activeTabs = getActiveTabs(selectedProductTypes);

  const tooltip = getTakeoverTooltip(takeover, fuelType);

  const handleCheck = (productType: ProductType) => (value: { target: { checked: boolean } }) => {
    if (selectedProductTypes.length === 1 && productType === selectedProductTypes[0]) {
      return;
    }

    const updatedValue =
      value.target.checked === false
        ? selectedProductTypes.filter(entry => entry !== productType)
        : selectedProductTypes.concat(productType);

    setSelectedProductTypes(updatedValue);
  };

  if (type === 'checkmark') {
    return (
      <CheckBoxWrapper>
        <Heading>Hvilke løsninger er du interesseret i?</Heading>

        <TypeSelectorCheckBox
          disabled={disabledTabs?.includes('heatPump')}
          selected={selectedProductTypes.includes('heatPump')}
          onClick={handleCheck('heatPump')}
          label='Varmepumpe'
          tooltip={tooltip}
        />

        <TypeSelectorCheckBox
          disabled={disabledTabs?.includes('solar')}
          selected={selectedProductTypes.includes('solar')}
          onClick={handleCheck('solar')}
          label='Solceller'
          tooltip={tooltip}
        />
      </CheckBoxWrapper>
    );
  }

  {
    return (
      <TabWrapper style={style}>
        <Background style={backgroundStyle} />

        <TypeSelectorTab
          label='Varmepumpe'
          selected={activeTabs === 'heatPump'}
          onClick={() => setSelectedProductTypes(['heatPump'])}
          disabled={disabledTabs?.includes('heatPump')}
          tooltip={tooltip}
          style={buttonStyle}
        />

        <TypeSelectorTab
          label='Solceller'
          selected={activeTabs === 'solar'}
          onClick={() => setSelectedProductTypes(['solar'])}
          disabled={disabledTabs?.includes('solar')}
          tooltip={tooltip}
          style={buttonStyle}
        />

        <TypeSelectorTab
          label='Kombi'
          selected={activeTabs === 'both'}
          onClick={() => setSelectedProductTypes(['solar', 'heatPump'])}
          disabled={(disabledTabs?.length ?? 0) > 0}
          tooltip={tooltip}
          style={buttonStyle}
        />
      </TabWrapper>
    );
  }
};

type TypeSelectorCheckBoxProps = {
  label: string;
  selected?: boolean;
  onClick: (value: { target: { checked: boolean } }) => void;
  disabled?: boolean;
  tooltip?: string;
};

const TypeSelectorCheckBox = ({ label, selected, onClick, disabled, tooltip }: TypeSelectorCheckBoxProps) => (
  <Tooltip placement='bottomRight' title={disabled ? tooltip : ''}>
    <Row disabled={disabled}>
      <Checkbox checked={selected} onChange={onClick} disabled={disabled} />

      <Heading color={disabled ? 'grey' : undefined} level={3}>
        {label}
      </Heading>
    </Row>
  </Tooltip>
);

type TypeSelectorTabProps = Omit<TypeSelectorCheckBoxProps, 'onClick'> & {
  onClick: () => void;
  style?: CSSProperties;
};

const TypeSelectorTab = ({ label, selected, onClick, disabled, tooltip, style }: TypeSelectorTabProps) => (
  <Tooltip placement='bottomRight' title={disabled ? tooltip : ''}>
    <TabButton style={style} disabled={disabled} onClick={disabled ? undefined : onClick}>
      <Text strong level={2} color='light' style={{ fontSize: '14px' }}>
        {label}
      </Text>
      {selected && <div style={{ borderBottom: selected ? `2px solid ${BodilColor.ui.orange}` : undefined }} />}
    </TabButton>
  </Tooltip>
);

const getActiveTabs = (selectedProductTypes: ProductTypes) => {
  if (selectedProductTypes.length > 1) {
    return 'both';
  }

  return selectedProductTypes.includes('solar') ? 'solar' : 'heatPump';
};

const getTakeoverTooltip = (takeover?: TakeoverType, fuelType?: string) => {
  const heating = fuelMap[fuelType ?? ''];

  switch (takeover) {
    case 'apartment': {
      return APARTMENT_TAKEOVER_TEXT;
    }

    case 'highUsage': {
      return HIGH_USAGE_TAKEOVER_TEXT(false);
    }

    case 'invalidHeating': {
      return INVALID_HEATING_TEXT(heating);
    }

    default: {
      return '';
    }
  }
};
